import api from './api'
import { ifMatch } from './dealFile'
import { toCamelCase } from '@/utils/transform'

/** The version of a loan with no terms saved yet; a first save names it too,
 *  so two people drafting a deal's first term sheet are checked (C23b). */
export const NO_STRUCTURE_VERSION = 'none'

export interface Covenant {
  id: string
  loanId: string
  covenantType: string
  threshold: number | null
  direction: 'min' | 'max'
  testFrequency: string
  curePeriodDays: number
  effectiveDate: string | null
  expiryDate: string | null
  description: string | null
  isActive: boolean
}

export interface LoanStructure {
  id: string
  loanId: string
  productType: string | null
  lienPosition: string | null
  rateType: string | null
  indexName: string | null
  spreadBps: number | null
  allInRate: number | null
  rateFloor: number | null
  rateCap: number | null
  termMonths: number | null
  amortMonths: number | null
  ioMonths: number | null
  extensionOptions: string | null
  prepaymentTerms: string | null
  recourse: string | null
  guaranty: string | null
  interestReserve: number | null
  capexReserve: number | null
  tiLcReserve: number | null
  originationFeeBps: number | null
  exitFeeBps: number | null
  equityKicker: string | null
  extraTerms: Record<string, unknown> | null
  /** The terms' version (also the ETag). Sent back as If-Match on save. */
  version?: string
  covenants: Covenant[]
}

/** Snake-cased upsert body — mirrors backend StructureUpsert exactly (no key mapping). */
export interface StructureUpsertBody {
  product_type?: string
  lien_position?: string
  rate_type?: string
  index_name?: string
  spread_bps?: number
  all_in_rate?: number
  rate_floor?: number
  rate_cap?: number
  term_months?: number
  amort_months?: number
  io_months?: number
  extension_options?: string
  prepayment_terms?: string
  recourse?: string
  guaranty?: string
  interest_reserve?: number
  capex_reserve?: number
  ti_lc_reserve?: number
  origination_fee_bps?: number
  exit_fee_bps?: number
  equity_kicker?: string
  extra_terms?: Record<string, unknown>
}

export const structuresService = {
  /** Returns null (not a thrown error) when no structure has been saved yet for this loan. */
  get: (loanId: string) =>
    api
      .get(`/structures/${loanId}`)
      .then((r) => toCamelCase<LoanStructure>(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) return null
        throw err
      }),

  /** `version` is the one the terms were loaded at (NO_STRUCTURE_VERSION when
   *  none were saved), sent as If-Match: a save over terms someone changed
   *  since is refused with 409 naming them, never silently overwritten. The
   *  server refuses a save that names no version (428). */
  upsert: (loanId: string, body: StructureUpsertBody, version: string | null) =>
    api.put(`/structures/${loanId}`, body, ifMatch(version)).then((r) => toCamelCase<LoanStructure>(r.data)),
}
